import React from 'react';
import { styled } from '@linaria/react';
import { Reorder } from 'framer-motion';
import { produce } from 'immer';

import { generateId } from '@/utils';
import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import ControlWrapper from '@/components/Controls/ControlWrapper';

import type { Color, ColorWithId } from './GradientGenerator.helpers';
import ColorUnit, { ColorUnitType } from './ColorUnit';

interface Props {
  id: string;
  colors: Array<ColorWithId>;
  setColors: React.Dispatch<React.SetStateAction<Array<ColorWithId>>>;
}

function ColorsControl({ id, colors, setColors }: Props) {
  const [editingColorIndex, setEditingColorIndex] = React.useState<
    number | null
  >(null);

  const hasMounted = React.useRef(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  // We don't want to allow the user to edit a color while they're
  // dragging and dropping. It leads to confusing/glitchy UX.
  const [isReordering, setIsReordering] = React.useState(false);

  React.useEffect(() => {
    hasMounted.current = true;
  }, []);

  const numOfValidColors = colors.filter(
    (item) => !!item.color
  ).length;

  function tweakColor(index: number, newColor: Color) {
    setColors((colors) => {
      return produce(colors, (draftColors) => {
        draftColors[index].color = newColor;
      });
    });
  }

  function deleteColor(index: number) {
    setColors((colors) => {
      // Don't allow deleting the last actual color.
      // This is a redundancy; it shouldn't even show the button
      // in this case!
      if (numOfValidColors <= 1) {
        return colors;
      }

      return produce(colors, (draftColors) => {
        draftColors.splice(index, 1);
        draftColors.push({
          color: null,
          id: generateId(),
        });
      });
    });
  }

  let addNewColorButtonIndex = colors.findIndex(
    (item) => !item.color
  );

  return (
    <ControlWrapper id={id} label="Colors" labelAs="span">
      <ColorList
        // Once again, TS struggles with `as`
        // @-ignore
        axis="x"
        values={colors}
        onReorder={(orderedColors) => {
          // We don't want to allow the `null` colors to be in front
          // of the actual values.
          const actualColors = orderedColors.filter((c) => !!c.color);
          const emptyColors = orderedColors.filter((c) => !c.color);
          setColors([...actualColors, ...emptyColors]);
        }}
      >
        {colors.map((item, index) => {
          const { id, color } = item;

          const isEditing = index === editingColorIndex;

          let type: ColorUnitType;
          if (color) {
            type = 'existing-color';
          } else if (index === addNewColorButtonIndex) {
            type = 'add-new-button';
          } else {
            type = 'empty';
          }

          return (
            <ColorUnit
              key={id}
              item={item}
              type={type}
              isEditing={isEditing}
              isReordering={isReordering}
              canBeDeleted={numOfValidColors > 1}
              hasMounted={hasMounted.current}
              prefersReducedMotion={prefersReducedMotion}
              onClick={() => {
                if (isReordering) {
                  return;
                }

                if (type === 'existing-color') {
                  setEditingColorIndex(isEditing ? null : index);
                } else if (type === 'add-new-button') {
                  tweakColor(index, {
                    hue: 0,
                    saturation: 1,
                    lightness: 0.5,
                  });
                  setEditingColorIndex(index);
                }
              }}
              onDismiss={() => {
                setEditingColorIndex(null);
              }}
              onDelete={() => {
                deleteColor(index);
              }}
              onDragStart={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();

                setIsReordering(true);

                // If the color tooltip is open, close it.
                // Otherwise, it'll weirdly switch to the other color
                // that occupies its position
                setEditingColorIndex(null);
              }}
              onDragEnd={() => {
                // Wait a small amount of time after dragging before
                // re-enabling color editing.
                window.setTimeout(() => {
                  setIsReordering(false);
                }, 150);
              }}
              setColor={(newColor: Color) => {
                tweakColor(index, newColor);
              }}
            />
          );
        })}
      </ColorList>
    </ControlWrapper>
  );
}

const ColorList = styled(Reorder.Group)`
  display: flex;
  gap: 16px;
  padding: 0;
  list-style-type: none;
  isolation: isolate;
`;

export default ColorsControl;
